import Backbone from 'backbone'
import _ from 'lodash'
import template from './forms-edit.hbs'
import DisplayHeader from 'scoreboard/components/display-header'
import tabs from 'scoreboard/components/view-edit-tabs.hbs'
import EditButtons from 'scoreboard/components/edit/edit-buttons'
import FormView from 'scoreboard/components/form-view'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import formHelper from 'scoreboard/components/form-helper'
import i18n from 'scoreboard/components/i18n'
import conductor from 'scoreboard/components/conductor'
import ObjectSelector from 'scoreboard/components/object-selector'
import * as datasetSelectorOptions from 'scoreboard/datasets/dataset-selector-options'
import dropdown from 'scoreboard/components/dropdown'
import datasetLabel from './dataset-label.hbs'
import selects from 'scoreboard/components/selects'
import guardConstants from 'scoreboard/components/unsaved/guard-constants'
import inputLengthValidatorFactory from 'scoreboard/components/validators/input-length-validator-factory'
import { triggerAddOrEdit } from 'scoreboard/components/domain-object-events'
import { isV6 } from 'scoreboard/components/version'
import formType from '../model/form-type'

const FormsEdit = Backbone.View.extend({

	tagName: 'form',

	className: 'table-hack display-layout forms-edit-view',

	events: {
		'click .cancel': 'onClickCancel'
	},

	/** @type {Form} */
	model: null,

	initialize: function(options = {}) {
		this.options = options
		if (this.model.isNew()) {
			this.model.set('label', i18n.get('forms.new'))
		}

		this.initializeFormView()

		this.pageOrder = _.map(this.model.get('pages'), p => p.id)
		this.listenTo(Backbone, 'move:page', ({ rootFormId, pageOrder }) => {
			if (rootFormId == this.model.get('rootFormId')) {
				this.pageOrder = pageOrder
				this.renderStartingPageSelect()
			}
		})
	},

	initializeFormView() {
		let MyFormView = FormView.extend({
			test: () => this.test()
		})

		this.formView = new MyFormView({
			toggleCancel: true,
			el: this.el,
			submit: this.submit.bind(this),
			guardOptions: {
				keepChangesFn: () => {
					const testMsg = this.test()
					if (!_.isNil(testMsg)) {
						// if there is an error, show it and hide the guard
						this.formView.errorMessage(testMsg)
						return guardConstants.HIDE_WARNING
					}
					// otherwise, save the form as normal before navigating away
					return this.submit()
				}
			}
		})
	},

	render: function() {
		this.createHeader()

		this.$el.empty().append(this.header.el, template())
		this.header.render()

		// set up starting page select (for existing forms only)
		this.renderStartingPageSelect()

		// display dataset label and starting page select (for existing forms)
		if (!this.model.isNew()) {
			this.$('.dataset-label-container').append(datasetLabel({
				label: _.get(this.model.attributes, 'dataset.label'),
				href: `datasets/${_.get(this.model.attributes, 'datasetId')}`
			}))
			this.$('.dataset-and-starting-page').removeClass('is-not-displayed')
		}

		formHelper.writeToDom(this.model, this.el)

		this.renderEditButtons()

		// set up dataset selector (for new forms only)
		this.renderDatasetSelector()

		// #55530 - validate labael length
		let labelLengthValidator = inputLengthValidatorFactory({
			high: 2000
		})
		this.labelField = new MarkedField({
			el: this.$('[name=label]'),
			validators: [requiredValidator, labelLengthValidator]
		}).render()

		this.formView.start({
			isNew: this.model.isNew(),
			primaryButtonEl: this.$('.save'),
		})

		this.renderBody()

		if (this.model.isNew()) {
			this.$('[name=label]').select()
		}

		return this
	},

	createHeader: function() {
		this.header = new DisplayHeader({
			heading: this.model.get('label') || i18n.get('common.create2', 'forms.new'),
			model: this.model,
			subsection: 'edit',
			// TODO: Remove when V5 is no longer supported
			// Only remove the isV6() check.
			tabs:  this.model.isNew() && isV6() ? '' : tabs({
				id: this.model.id || this.model.get('parentId'),
				section: 'forms',
				overviewTabCode: 'common.view',
				hideInfo: true,
				hideAlerts: true
			})
		})
	},

	renderDatasetSelector: function() {
		// set up dataset selector (for new forms only)
		if (this.model.isNew()) {
			this.objectSelector = new ObjectSelector(datasetSelectorOptions.forSelector()).render()

			this.$('.dataset-panel')
				.removeClass('is-not-displayed')
				.append(this.objectSelector.el)

			this.listenTo(this.objectSelector, 'select', (dataset) => {
				this.onSelectDataset(dataset)
			})

			this.objectSelector.on('select selectOrg', () => this.formView.change())
		}
	},

	renderStartingPageSelect: function() {
		if (this.model.isNew()) return
		const startingFormId = this.model.get('startingFormId')
		const startingFormIdSelect = this.$('[name=startingFormId]')

		// sort the pages so they appear the same here as they do in the nav pane
		let pages = _.get(this.model.attributes, 'pages', [])
		pages = _.sortBy(pages, item =>
			_.indexOf(this.pageOrder, item.id))

		// #59888
		// Do not allow dialogs to be starting pages
		pages = pages.filter(item => item.type !== formType.DIALOG)

		// If there's no starting page, show a blank entry in the select
		if (_.isNil(startingFormId)) {
			pages = [{ label: '' }, ...pages]
		}

		dropdown.idLabels(startingFormIdSelect.empty(), pages)
		!_.isNil(startingFormId) &&
		selects.val(startingFormIdSelect, startingFormId)
	},

	renderEditButtons: function() {
		// set up buttons
		this.editButtons = new EditButtons({
			model: this.model,
			pasteAllowed: false
		})

		if (this.model.isNew()) {
			// put the buttons at the bottom to make room for the dataset selector
			this.$('.edit-buttons-container')
				.append(this.editButtons.$el.addClass('edit-buttons-bottom'))
		} else {
			this.$('.edit-buttons').replaceWith(this.editButtons.el)
		}

		this.editButtons.render()
	},

	renderBody: function() {
		if (this.model.isNew()) {
			return
		}
		// display 'add page' button
		this.$('.toolbar').removeClass('is-not-displayed')
	},

	context: function() {
		let datasetLabel = _.get(this.model.attributes, 'dataset.label')
		return { datasetLabel }
	},

	onSelectDataset: function(dataset) {
		this.formView.clearErrors()
		this.model.set('dataset', dataset)
		this.model.set('datasetId', dataset.id)
		this.formView.change()
	},

	onClickCancel: function() {
		if (this.model.isNew()) {
			if (conductor.id()) {
				conductor.edit()
			} else {
				let url = `organizations/${this.model.get('organizationId')}/forms`
				conductor.navigate(url, { trigger: true })
			}
		} else {
			this.render()
		}
	},

	test: function() {
		if (this.model.isNew()) {
			// #59939
			// Ensure the dataset being submitted has been selected to prevent
			// a default dataset from being saved (such as with dialogs)
			let dataset = this.objectSelector.selected()
			if (_.isNil(dataset) || _.get(dataset, 'type') !== 'dataset') {
				return i18n.get('common.error.mustSelect.dataset')
			}
		}
	},

	getData() {
		let data = _.extend({}, this.model.attributes,
			formHelper.readFromDom(this.el))

		// #57566 do not include the dataset in the save request
		return _.omit(data, 'dataset')
	},

	submit: function(e) {
		let action = this.model.isNew() ? 'add' : 'edit'

		let data = this.getData()

		let url = _.result(this.model, 'url')
		return this.model.save(data, {
			url,
			patch: true,
			success: function(model, response) {
				this.onSubmitSuccess(model, action)
			}.bind(this),
			error: (model, xhr) => this.formView.error(xhr)
		})
	},

	onSubmitSuccess(model, action) {
		this.trigger('save', model)
		triggerAddOrEdit(action, model)
		this.formView.start({isNew: false})
		this.render({instadone: true})
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.header, this.labelField, this.editButtons,
				this.addFormWidgetTooltip, this.objectSelector], 'remove')
	}
})

export default FormsEdit
FormsEdit.__filename__ = 'forms/edit/forms-edit.js'
